<script setup>
    import { useSitesStore } from '@/stores/sites';
    import { useToursStore } from '@/stores/tours';
    import { useTrailsStore } from '@/stores/trails';
    import { usePointsOfInterestStore } from '@/stores/points-of-interest';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { updateHead } from '@/composables/helpers/updateHead';

    import { storeToRefs } from 'pinia';

    // STORE ----------------------------------------------------------------
    const toursStore = useToursStore();
    const tours = toursStore.toursData;

    const trailsStore = useTrailsStore();
    const trails = trailsStore.trailsData;

    const sitesStore = useSitesStore();
    // const sites = sitesStore.sitesData;
    const { sites } = storeToRefs(sitesStore);
    console.log(`🍤 ~ sites:`, sites);
    console.log(`🍤 ~ sites:`, sites.value);
    const test = sites.value.find((site) => site.id === 'recJJCJyQMGXvbd5u');
    console.log(`🍤 ~ test:`, test);

    const neighborhoodsStore = useNeighborhoodsStore();
    const neighborhoods = neighborhoodsStore.neighborhoodsData;

    const pointsOfInterestStore = usePointsOfInterestStore();
    const pointsOfInterest = pointsOfInterestStore.pointsOfInterestData;
    // ---------------------------------------------------------------------

    const route = useRoute();
    const router = useRouter();

    const mapView = ref(false);
    const showDirections = ref(false);

    // Get Tour by Id
    const pathId = route.params.tour[0];
    const tourData = tours?.records?.find((tour) => tour.id === pathId);

    // Get Type of Tour  ( Listicle = Themed, Trails = Neighborhoods)
    const dataType = computed(() => {
        return tourData?.fields['Type'] === 'Listicle' ? 'site' : 'generic';
    });

    // Get data from Trails feed related to the Tour
    const tourTrails = trails.filter((el) => {
        return el?.fields?.Trail && el?.fields?.Trail[0] === pathId;
    });

    // Get Stops/Sites on Tour
    const stopsOnTour = computed(() => {
        let tourStops = [];
        if (tourData?.fields['Type'] === 'Listicle' && sites.value.length) {
            console.log(`🍤 ~ stopsOnTour ~ sites:`, sites.value);
            const siteIds = tourData?.fields['Organizations'];
            console.log(`🍤 ~ stopsOnTour ~ siteIds:`, siteIds);
            if (siteIds && siteIds.length) {
                tourStops = sites.value.filter((el) => {
                    return siteIds.includes(el.id);
                });
            }
        } else {
            tourStops = tourTrails;
        }
        return tourStops;
    });

    const tourPhotos = computed(() => {
        let photoData = {
            photos: [],
            credits: [],
        };
        if (stopsOnTour?.value?.length) {
            if (tourData?.fields['Type'] === 'Trail') {
                tourTrails.forEach((el) => {
                    const field = el.fields['Photos'];
                    if (field && field.length) {
                        photoData.photos.push(field[field.length - 1]);
                        photoData.credits.push(field[field.length - 1]?.filename);
                    }
                });
            } else {
                stopsOnTour.value.forEach((el) => {
                    const field = el.fields['Selected Photos'];
                    if (field && field.length) {
                        photoData.photos.push(field[field.length - 1]);
                        photoData.credits.push(field[field.length - 1].filename);
                    }
                });
            }
        }
        return photoData;
    });

    // Get Neighborhood Data for Tour if there is one
    const neighborhood = computed(() => {
        if (tourData?.fields['OHC Neighborhood']) {
            const id = tourData?.fields['OHC Neighborhood'][0];
            return neighborhoods.find((neighborhood) => neighborhood.id === id);
        }
        return false;
    });

    // Get Points of Interest by Neighborhood
    const pointsOfInterestByNeighborhood = computed(() => {
        let points = [];
        if (neighborhood?.value && pointsOfInterest && pointsOfInterest.length > 0) {
            points = pointsOfInterest.filter((item) => {
                if (item.fields?.Neighborhoods) {
                    return item.fields.Neighborhoods.find((id) => id === neighborhood.value.id);
                }
            });
        }
        return points;
    });

    // open Directions
    function openDirections() {
        showDirections.value = true;
    }

    function hideDirections() {
        showDirections.value = false;
    }

    onMounted(() => {
        if (tourData) {
            const { track } = useAnalytics();
            track('tour_viewed', { tour_id: tourData.id, tour_name: tourData.fields?.['Trail Name'] || '' });
        }
    });

    const desc = tourData?.fields?.Description || '';
    const frontEnd = useRuntimeConfig().public.clientFrontend;

    updateHead({
        title: tourData?.fields?.['Trail Name'] || '',
        description: desc.slice(0, 150),
        path: route.path,
        // Add image once data is updated
        image: frontEnd + '/img/ohc-share.jpg',
    });
</script>

<template>
    <article class="tour-detail" v-if="tourData" :class=" mapView ? 'map-view' : '' ">
        <button class="map-toggle top-actions" @click="mapView = !mapView">
            <SvgsMapWhiteCircle v-show="!mapView" />
            <SvgsListWhiteCircle v-show="mapView" />
        </button>

        <BlocksTitleWithMapAndCards v-if="mapView && stopsOnTour.length > 0" :title="tourData?.fields['Name']" :cards="stopsOnTour" :content="dataType" />

        <template v-if="!mapView">
            <BlocksImageryHero v-if="tourPhotos && tourPhotos.photos.length > 0" :photos="tourPhotos.photos" :credits="tourPhotos.credits" />
            <div class="hd-container">
                <section class="header-details container">
                    <h1 class="headline-1">{{ tourData.fields['Name'] }}</h1>
                    <p v-if="tourData.fields['Neighborhood'] && dataType === 'site'" class="gray-tag">
                        {{ tourData.fields['Neighborhood'] }}
                    </p>
                    <div class="small-label" v-if="dataType === 'generic'">
                        <p class="gray-tag">Neighborhood</p>
                        <SvgsAudioTourGray />
                    </div>
                    <p class="gray-tag" v-if="dataType === 'site'">Themed</p>
                </section>

                <BlocksDetailDescription class="description" v-if="tourData.fields['Description']">
                    <p v-html="tourData.fields['Description']"></p>
                </BlocksDetailDescription>

                <GlobalActions
                    class="actions"
                    :item="tourData"
                    :useItinerary="false"
                    :useDirections="false"
                    :useDirectionsSingle="false"
                    list="ohcFavoriteTours"
                    useShare
                    useFavorite
                    @openDirections="openDirections" />

                <div v-if="showDirections && stopsOnTour?.length > 0" class="directions-section container">
                    <Directions :cards="stopsOnTour" canToggleDirections @hideDirections="hideDirections" />
                </div>

                <BlocksTourStopsList :items="stopsOnTour" :dataType="dataType" v-if="stopsOnTour && stopsOnTour.length > 0" />

                <div class="container neighborhood" v-if="neighborhood">
                    <p class="subhead-2">Explore the Neighborhood Area</p>
                    <CardsNeighborhood :item="neighborhood" />
                </div>

                <BlocksLikeALocalCarousel v-if="neighborhood && pointsOfInterestByNeighborhood.length > 0" :recommendations="pointsOfInterestByNeighborhood" />

            </div>
        </template>
    </article>
</template>

<style lang="scss">
    .tour-detail {
        position: relative;

        // imagery-hero.vue gives .height-container `margin-top: 60px` at $tablet
        // to clear the floating toggle. Sites/single.vue already zeroes it for
        // the same reason — see the longer note there — and this page needs it
        // too, for a worse version of the problem.
        //
        // .site-detail is a flex column, so that margin cannot collapse and just
        // pushed its carousel down. .tour-detail is a plain block, so the margin
        // collapses straight up and out of the article — through the carousel,
        // through .tour-detail, into main's content box — and moved the ENTIRE
        // page down 60px. That is why every computed margin-top in the chain
        // read 0 while the article still started 60px below main: the gap
        // belonged to a descendant that had escaped.
        //
        // The toggle it was clearing is absolutely positioned over the hero, so
        // nothing needs the offset. Four classes with the scope attribute
        // outrank imagery-hero's own scoped rule.
        .carousel-component .height-container {
            margin-top: 0;
        }

        &.map-view #title-map-header {
            padding-top: 58px;
            padding-bottom: 16px;
        }
        .map-toggle {
            position: absolute;
            top: 16px;
            z-index: 57;
            right: 15px;
        }

        .header-details {
            margin-top: 18px;
            .headline-1 {
                margin-bottom: 18px;
            }
        }

        .tour-type {
            padding: 6px 10px 4px;
            font-size: 12px;
            font-weight: 800;
            line-height: 1em;
            letter-spacing: 0.96px;
            text-transform: uppercase;
            background-color: $turquoise-light;
            display: inline-block;
            margin-top: 12px;
        }

        .actions,
        .directions-section {
            margin-top: 32px;
        }

        .directions-section .description {
            margin: 36px 0;
        }

        .gray-tag {
            margin: 0 0 16px;
        }

        .address {
            margin-top: 4px;
        }

        .flex-row {
            display: flex;
            align-items: center;
        }

        .small-heavy {
            text-transform: uppercase;
        }
        .body-book.heavy,
        .small-book.heavy {
            font-weight: 800;
        }

        .details {
            margin-top: 32px;
            .title {
                font-size: 14px;
                font-weight: 800;
                line-height: 21px;
                letter-spacing: 1.12px;
                margin-bottom: 8px;
            }
        }

        .small-label {
            display: flex;
            p {
                margin-right: 10px;
            }
            svg {
                transform: translateY(-2px);
            }
        }

        .neighborhood {
            margin-top: 60px;
            .subhead-2 {
                margin-bottom: 20px;
            }
        }

        .description {
            a {
                text-decoration: underline;

                &:hover {
                    color: $red;
                }
            }
        }

        @media (min-width: $tablet) {
            width: 100vw;
            // $hd, as every other detail template uses — this was hardcoded to
            // 1118px ($hd minus 32), so the itinerary body sat 32px narrower
            // than the site, event and neighborhood pages.
            max-width: $hd;
            margin: auto;
        }
    }
</style>
